import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Box, Typography, Menu, MenuItem } from '@mui/material';
import { CustomButton } from 'components/Button/Button';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import NoteIcon from 'assets/images/NoteIcon.svg';
import SingleDatePicker from 'components/dateRange/SingleDatePicker';
import { AuditSummaryProps, ShiftOption } from '../../types';
import { formatShiftTime } from '../../utils/dateUtils';
import moment from 'moment';
import '../../style/AuditSummary.scss';

import AuditVerificationDialog from './AuditVerificationDialog';
import AddNote from './AddNote';
import ManualEntryConfirmationDialog from './ManualEntryConfirmationDialog';
import { getManualDayEntryEditability } from './manualDayEntry';
import { buildAuditSummaryContext } from './summary/context';
import { useManualEntryController } from './summary/hooks/useManualEntryController';
import { useUpsertStorePosManualDayEntry } from '../../hooks';
import { buildScratcherSalesTile } from './summary/builders/buildScratcherSalesTile';
import { buildLottoSalesTile } from './summary/builders/buildLottoSalesTile';
import { buildPayoutsTile } from './summary/builders/buildPayoutsTile';
import { buildOverallTile } from './summary/builders/buildOverallTile';
import { ScratcherSalesTile } from './summary/tiles/ScratcherSalesTile';
import { LottoSalesTile } from './summary/tiles/LottoSalesTile';
import { PayoutsTile } from './summary/tiles/PayoutsTile';
import { OverallTile } from './summary/tiles/OverallTile';

type ShiftLabelView = Pick<ShiftOption, 'label' | 'labelParts'>;

const getShiftTimeSeconds = (timeStr: string) => {
    const [h = '0', m = '0', s = '0'] = timeStr.split(':');
    return (Number(h) * 3600) + (Number(m) * 60) + Number(s);
};

const getShiftDateLabel = (shiftMoment: moment.Moment | null, auditDate: string | null) => {
    if (!shiftMoment || !auditDate || shiftMoment.format('YYYY-MM-DD') === auditDate) {
        return null;
    }

    return shiftMoment.format('MM/DD');
};

const formatAuditLocationLabel = (locationName?: string | null) => {
    const trimmed = locationName?.trim();
    if (!trimmed) {
        return null;
    }

    return trimmed.toLowerCase().startsWith('on ') ? trimmed : `On ${trimmed}`;
};

const buildShiftLabelParts = ({
    startTime,
    endTime,
    startMoment,
    endMoment,
    auditDate,
    locationLabel,
}: {
    startTime: string;
    endTime: string;
    startMoment: moment.Moment | null;
    endMoment: moment.Moment | null;
    auditDate: string | null;
    locationLabel?: string | null;
}) => {
    const startTimeLabel = formatShiftTime(startTime);
    const endTimeLabel = formatShiftTime(endTime);
    const startDateLabel = getShiftDateLabel(startMoment, auditDate);
    const endDateLabel = getShiftDateLabel(endMoment, auditDate);

    const labelParts = [
        { text: startTimeLabel },
        ...(startDateLabel ? [{ text: ` (${startDateLabel})`, muted: true, small: true }] : []),
        { text: ' - ' },
        { text: endTimeLabel },
        ...(endDateLabel ? [{ text: ` (${endDateLabel})`, muted: true, small: true }] : []),
        ...(locationLabel ? [{ text: ` (${locationLabel})`, muted: true, small: true }] : []),
    ];

    return {
        label: labelParts.map((part) => part.text).join(''),
        labelParts,
    };
};

const ShiftLabelText: React.FC<{ option: ShiftLabelView }> = ({ option }) => {
    const parts = option.labelParts?.length ? option.labelParts : [{ text: option.label }];

    return (
        <Box component="span" sx={{ display: 'inline', whiteSpace: 'nowrap' }}>
            {parts.map((part, index) => (
                <Typography
                    component="span"
                    key={`${part.text}-${index}`}
                    sx={{
                        color: part.muted ? '#7A8497' : 'inherit',
                        fontSize: part.small ? '0.74em' : 'inherit',
                        fontWeight: part.small ? 500 : 'inherit',
                        lineHeight: 'inherit',
                    }}
                >
                    {part.text}
                </Typography>
            ))}
        </Box>
    );
};

const AuditSummary: React.FC<AuditSummaryProps> = ({
    auditsByDateModel,
    selectedShift = 'All Shift',
    onShiftChange,
    date,
    onDateChange,
    auditTotals,
    dailyTotals,
    selectedAuditId,
    isLoading = false,
    isLoadingStores = false,
    isLoadingAcknowledgeAndVerified = false,
    hideCards = false,
    isBothConnected = false,
    isBothNotConnected = false,
    isLottoConnectedOnly = false,
    isPOSConnectedOnly = false,
    isPosValueMissing = false,
    storeId,
    storeUserRole,
    canApproveReconciliation = false,
    acknowledgeAndVerifiedModel
}) => {
    type ManualEntryConfirmationAction = 'save' | 'remove';
    const [openSavePosDataConfirmation, setOpenSavePosDataConfirmation] = useState(false);
    const [posInputValues, setPosInputValues] = useState({
        scratcherSales: '',
        scratcherQty: '',
        lottoSales: '',
        payouts: '',
    });
    const [shiftAnchorEl, setShiftAnchorEl] = useState<null | HTMLElement>(null);
    const [openVerificationDialog, setOpenVerificationDialog] = useState(false);
    const [verificationMode, setVerificationMode] = useState<'verify' | 'ack'>('verify');
    const [openAddNoteDialog, setOpenAddNoteDialog] = useState(false);
    const [manualEntryConfirmationAction, setManualEntryConfirmationAction] = useState<ManualEntryConfirmationAction | null>(null);
    const [pendingVerified, setPendingVerified] = useState(false);
    const [pendingAck, setPendingAck] = useState(false);

    // Local state for verification/acknowledgment to allow toggling before saving
    const [localVerified, setLocalVerified] = useState(false);
    const [localAck, setLocalAck] = useState(false);

    // Get verified status from new acknowledge and verified model
    const isVerifiedFromAPI = acknowledgeAndVerifiedModel?.isVerified || false;
    const isAckFromAPI = acknowledgeAndVerifiedModel?.isAck || false;
    const verifiedNote = acknowledgeAndVerifiedModel?.verificationNote || '';
    const managerNote = acknowledgeAndVerifiedModel?.managerNote || '';
    const hasNote = managerNote.length > 0;
    const normalizedStoreRole = String(storeUserRole || '').toLowerCase();
    const canShowVerifyAction = acknowledgeAndVerifiedModel
        ? acknowledgeAndVerifiedModel.canVerify === true
        : normalizedStoreRole === 'owner' ||
            normalizedStoreRole === 'reco_officer' ||
            normalizedStoreRole === 'reconciliation_manager' ||
            (normalizedStoreRole === 'manager' && canApproveReconciliation);
    const canShowManagerNoteAction = normalizedStoreRole === 'manager' && !canApproveReconciliation && acknowledgeAndVerifiedModel?.canAddManagerNote === true;

    // Sync local state with API data when it arrives
    useEffect(() => {
        if (acknowledgeAndVerifiedModel) {
            setLocalVerified(acknowledgeAndVerifiedModel.isVerified);
            setLocalAck(acknowledgeAndVerifiedModel.isAck);
        } else {
            setLocalVerified(false);
            setLocalAck(false);
        }
    }, [acknowledgeAndVerifiedModel]);

    const handleShiftClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
        setShiftAnchorEl(event.currentTarget);
    }, []);

    const handleShiftClose = useCallback(() => {
        setShiftAnchorEl(null);
    }, []);

    const handleShiftSelect = useCallback((shift: string) => {
        onShiftChange(shift);
        handleShiftClose();
    }, [onShiftChange, handleShiftClose]);

    const handlePosInputChange = useCallback((
        field: 'scratcherSales' | 'scratcherQty' | 'lottoSales' | 'payouts',
        nextValue: string
    ) => {
        if (!/^\d*(\.\d{0,2})?$/.test(nextValue)) {
            return;
        }

        setPosInputValues((prev) => ({
            ...prev,
            [field]: nextValue,
        }));
    }, []);

    const isAllShift = selectedShift === 'All Shift';
    const businessDate = useMemo(() => (
        date ? moment(date).format('YYYY-MM-DD') : null
    ), [date]);

    useEffect(() => {
        setPosInputValues({
            scratcherSales: '',
            scratcherQty: '',
            lottoSales: '',
            payouts: '',
        });
    }, [storeId, selectedShift, businessDate, isPosValueMissing]);

    const manualDayEntryEditability = useMemo(() => getManualDayEntryEditability({
        isBothNotConnected,
        isLottoConnectedOnly,
        isPOSConnectedOnly,
    }), [isBothNotConnected, isLottoConnectedOnly, isPOSConnectedOnly]);

    const {
        isLoadingManualDayEntry,
        isSavingManualDayEntry,
        manualFormValues,
        handleManualFieldChange,
        handleSaveManualEntries,
        handleRemoveManualEntries,
    } = useManualEntryController({
        storeId,
        businessDate,
        isAllShift,
        isLoadingStores,
        isBothConnected,
        editability: manualDayEntryEditability,
    });

    const upsertPosManualDayEntryMutation = useUpsertStorePosManualDayEntry(storeId, businessDate);

    const isSavePosDataDisabled = useMemo(() => (
        !posInputValues.scratcherSales.trim()
        || !posInputValues.scratcherQty.trim()
        || !posInputValues.lottoSales.trim()
        || !posInputValues.payouts.trim()
    ), [
        posInputValues.lottoSales,
        posInputValues.payouts,
        posInputValues.scratcherQty,
        posInputValues.scratcherSales,
    ]);

    const summaryContext = useMemo(() => buildAuditSummaryContext({
        selectedShift,
        date,
        auditTotals,
        dailyTotals,
        storeId,
        isLoading,
        isLoadingStores,
        isLoadingAcknowledgeAndVerified,
        hideCards,
        isBothConnected,
        isBothNotConnected,
        isLottoConnectedOnly,
        isPOSConnectedOnly,
        isPosValueMissing,
        posMissingScratcherSalesInput: posInputValues.scratcherSales,
        posMissingScratcherQtyInput: posInputValues.scratcherQty,
        posMissingLottoSalesInput: posInputValues.lottoSales,
        posMissingPayoutInput: posInputValues.payouts,
        manualFormValues,
    }), [
        selectedShift,
        date,
        auditTotals,
        dailyTotals,
        storeId,
        isLoading,
        isLoadingStores,
        isLoadingAcknowledgeAndVerified,
        hideCards,
        isBothConnected,
        isBothNotConnected,
        isLottoConnectedOnly,
        isPOSConnectedOnly,
        isPosValueMissing,
        posInputValues.scratcherSales,
        posInputValues.scratcherQty,
        posInputValues.lottoSales,
        posInputValues.payouts,
        manualFormValues,
    ]);

    const scratcherTileConfig = useMemo(() => buildScratcherSalesTile(summaryContext), [summaryContext]);
    const lottoSalesTileConfig = useMemo(() => buildLottoSalesTile(summaryContext), [summaryContext]);
    const payoutsTileConfig = useMemo(() => buildPayoutsTile(summaryContext), [summaryContext]);
    const overallTileConfig = useMemo(() => buildOverallTile(summaryContext), [summaryContext]);


    // Process shifts and operators from model
    const { shifts, operators } = useMemo(() => {
        if (!auditsByDateModel || !auditsByDateModel.hasAudits) {
            return { shifts: [{ label: 'All Shifts', value: 'All Shift' }] as ShiftOption[], operators: '' };
        }

        // Create shift options with unique shifts (group by time range to avoid duplicates)
        const shiftOptions: ShiftOption[] = [
            { label: 'All Shifts', value: 'All Shift' }
        ];

        // Get full audits data to access operator_name and time
        const allAudits = auditsByDateModel.getAudits();

        // Create shift options with proper labels
        const shiftDataWithLabels = allAudits.map((audit) => {
            const locationLabel = formatAuditLocationLabel(audit.audit_location?.location_name);
            // Check if both shift times are null
            if (!audit.shift_start_time && !audit.shift_end_time) {
                // Format time from ISO string to readable format (time only, no date) with timezone preservation
                const formattedTime = moment.parseZone(audit.time).format('h:mm a');
                return {
                    label: `${audit.operator_name} @ ${formattedTime}${locationLabel ? ` (${locationLabel})` : ''}`,
                    value: audit.audit_id,
                    isAutogenerated: Boolean(audit.is_autogenerated ?? (audit as any).isAutogenerated),
                    startTime: null,
                    auditId: audit.audit_id,
                    timeValue: audit.time, // Store original time for sorting
                    sortStartMoment: moment.parseZone(audit.time)
                };
            } else {
                const endDate = audit.time ? moment.parseZone(audit.time) : null;
                const explicitEndDate = (audit as any).shift_end_date
                    ? moment.parseZone((audit as any).shift_end_date)
                    : null;
                const explicitStartDate = (audit as any).shift_start_date
                    ? moment.parseZone((audit as any).shift_start_date)
                    : null;
                const startSeconds = getShiftTimeSeconds(audit.shift_start_time || '00:00:00');
                const endSeconds = getShiftTimeSeconds(audit.shift_end_time || '00:00:00');
                const crossesMidnight = endSeconds < startSeconds;

                const resolvedEndDate = explicitEndDate || endDate;
                const startDate = explicitStartDate || (
                    resolvedEndDate
                        ? (crossesMidnight ? resolvedEndDate.clone().subtract(1, 'day') : resolvedEndDate.clone())
                        : null
                );
                const startMoment = startDate
                    ? moment.parseZone(
                        `${startDate.format('YYYY-MM-DD')}T${audit.shift_start_time || '00:00:00'}`
                    )
                    : null;
                const endMoment = resolvedEndDate
                    ? moment.parseZone(
                        `${resolvedEndDate.format('YYYY-MM-DD')}T${audit.shift_end_time || '00:00:00'}`
                    )
                    : null;
                const shiftLabel = buildShiftLabelParts({
                    startTime: audit.shift_start_time || '',
                    endTime: audit.shift_end_time || '',
                    startMoment,
                    endMoment,
                    auditDate: businessDate,
                    locationLabel,
                });

                // Use existing format for shifts with times
                return {
                    label: shiftLabel.label,
                    labelParts: shiftLabel.labelParts,
                    value: audit.audit_id,
                    isAutogenerated: Boolean(audit.is_autogenerated ?? (audit as any).isAutogenerated),
                    startTime: audit.shift_start_time || '',
                    auditId: audit.audit_id,
                    timeValue: null, // Not needed for sorting when we have startTime
                    sortStartMoment: startMoment
                };
            }
        });

        // Sort shifts by full start datetime (ascending), not just time-of-day.
        const sortedShifts = [...shiftDataWithLabels].sort((a, b) => {
            if (a.sortStartMoment && b.sortStartMoment) {
                return a.sortStartMoment.valueOf() - b.sortStartMoment.valueOf();
            }
            if (a.sortStartMoment) return -1;
            if (b.sortStartMoment) return 1;
            return 0;
        });

        // Add sorted shifts to options
        sortedShifts.forEach((shiftData) => {
            shiftOptions.push({
                label: shiftData.label,
                value: shiftData.value,
                isAutogenerated: shiftData.isAutogenerated,
                labelParts: shiftData.labelParts,
            });
        });

        // Use model method to get operators string
        // When "All Shift" is selected, pass undefined to show all operators
        // Otherwise, use selectedShift (which contains the audit ID)
        const auditIdForOperators = isAllShift ? undefined : selectedShift;
        const operatorsString = auditsByDateModel.getOperatorsString(auditIdForOperators);

        return {
            shifts: shiftOptions,
            operators: operatorsString
        };
    }, [auditsByDateModel, selectedShift, isAllShift, businessDate]);

    const displayShift = useMemo(() => {
        if (isAllShift) return { label: 'All Shifts', isAutogenerated: false };
        const shift = shifts.find(s => s.value === selectedShift);
        // If shift is found, return its label
        if (shift) return { label: shift.label, labelParts: shift.labelParts, isAutogenerated: Boolean(shift.isAutogenerated) };
        // If shift not found but selectedAuditId matches selectedShift (data still loading), 
        // show loading state or keep selectedShift value until data loads
        if (selectedAuditId && selectedAuditId === selectedShift) {
            // Data is still loading, return a loading indicator or the auditId
            return { label: isLoading ? 'Loading...' : selectedShift, isAutogenerated: false };
        }
        // Fallback to 'All Shift' if no match
        return { label: 'All Shifts', isAutogenerated: false };
    }, [selectedShift, shifts, isAllShift, selectedAuditId, isLoading]);

    const handleOpenManualEntryConfirmation = useCallback((action: ManualEntryConfirmationAction) => {
        setManualEntryConfirmationAction(action);
    }, []);

    const handleCloseManualEntryConfirmation = useCallback(() => {
        setManualEntryConfirmationAction(null);
    }, []);

    const handleConfirmManualEntryAction = useCallback(async () => {
        if (!manualEntryConfirmationAction) return;

        if (manualEntryConfirmationAction === 'remove') {
            await handleRemoveManualEntries();
        } else {
            await handleSaveManualEntries();
        }

        setManualEntryConfirmationAction(null);
    }, [handleRemoveManualEntries, handleSaveManualEntries, manualEntryConfirmationAction]);

    const manualEntryConfirmationContent = useMemo(() => {
        if (manualEntryConfirmationAction === 'remove') {
            return {
                title: 'Remove Manual Entries',
                message: 'Are you sure you want to remove manual entries?',
                confirmButtonText: 'Remove',
            };
        }

        if (manualEntryConfirmationAction === 'save') {
            return {
                title: 'Save Manual Entries',
                message: 'Are you sure you want to save manual entries?',
                confirmButtonText: 'Save',
            };
        }

        return null;
    }, [manualEntryConfirmationAction]);

    const renderAddNoteButton = () => {
        return (
            <Box
                className="add-note-button"
                sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', mt: '0 !important' }}
                onClick={() => setOpenAddNoteDialog(true)}
            >
                <img src={NoteIcon} alt={hasNote ? "View Note" : "Add Note"} />
                <Typography>{hasNote ? "View Note" : "Add Note"}</Typography>
            </Box>
        );
    };

    const handleRequestVerificationDialog = useCallback(({
        mode,
        nextVerified,
        nextAck,
    }: {
        mode: 'verify' | 'ack';
        nextVerified: boolean;
        nextAck: boolean;
    }) => {
        setPendingVerified(nextVerified);
        setPendingAck(nextAck);
        setVerificationMode(mode);
        setOpenVerificationDialog(true);
    }, []);

    const handleConfirmSavePosData = useCallback(async () => {
        if (!storeId || !businessDate) {
            return;
        }

        await upsertPosManualDayEntryMutation.mutateAsync({
            business_date: businessDate,
            scratcher_qty: posInputValues.scratcherQty === '' ? null : Number(posInputValues.scratcherQty),
            scratcher_sales: posInputValues.scratcherSales === '' ? null : Number(posInputValues.scratcherSales),
            lotto_sales: posInputValues.lottoSales === '' ? null : Number(posInputValues.lottoSales),
            payout: posInputValues.payouts === '' ? null : Number(posInputValues.payouts),
        });

        setOpenSavePosDataConfirmation(false);
    }, [businessDate, posInputValues.lottoSales, posInputValues.payouts, posInputValues.scratcherQty, posInputValues.scratcherSales, storeId, upsertPosManualDayEntryMutation]);
    return (
        <>
            <Box className="audit-summary-container">
                {/* Left side info */}
                <Box className="audit-summary-info">
                    <Box className="info-row">
                        <Typography className="info-label">Date:</Typography>
                        <Box className="info-value single-date-32">
                            <SingleDatePicker
                                value={date}
                                onChange={onDateChange}
                                label="Select Date"
                                disabled={isLoading}
                            />
                        </Box>
                    </Box>
                    {!hideCards && (
                        <>
                            <Box className="info-row">
                                <Typography className="info-label">Shift:</Typography>
                                <Box className="info-value" onClick={handleShiftClick}>
                                    {isLoading ? (
                                        <div className="audit-skeleton skeleton-info" />
                                    ) : (
                                        <>
                                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.75 }}>
                                                <ShiftLabelText option={displayShift} />
                                                {displayShift.isAutogenerated && (
                                                    <span className="audit-table__auto-shift-badge">
                                                        Auto
                                                    </span>
                                                )}
                                            </Box>
                                            <KeyboardArrowDownIcon className="dropdown-icon" />
                                        </>
                                    )}
                                </Box>
                                <Menu
                                    anchorEl={shiftAnchorEl}
                                    open={Boolean(shiftAnchorEl)}
                                    onClose={handleShiftClose}
                                    className="shift-dropdown-menu"
                                >
                                    {shifts.length > 0 ? (
                                        shifts.map((shift) => {
                                            const isActive = shift.value === selectedShift;
                                            return (
                                                <MenuItem
                                                    key={shift.value}
                                                    onClick={() => handleShiftSelect(shift.value)}
                                                    sx={{
                                                        backgroundColor: isActive ? '#E3F2FD' : 'transparent',
                                                        color: isActive ? '#1976D2' : 'inherit',
                                                        fontWeight: isActive ? 600 : 400,
                                                        '&:hover': {
                                                            backgroundColor: isActive ? '#BBDEFB' : '#F5F5F5',
                                                        },
                                                    }}
                                                >
                                                    <Box sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.75 }}>
                                                        <ShiftLabelText option={shift} />
                                                        {shift.isAutogenerated && (
                                                            <span className="audit-table__auto-shift-badge">
                                                                Auto
                                                            </span>
                                                        )}
                                                    </Box>
                                                </MenuItem>
                                            );
                                        })
                                    ) : (
                                        <MenuItem
                                            onClick={() => handleShiftSelect('All Shift')}
                                            sx={{
                                                backgroundColor: selectedShift === 'All Shift' ? '#E3F2FD' : 'transparent',
                                                color: selectedShift === 'All Shift' ? '#1976D2' : 'inherit',
                                                fontWeight: selectedShift === 'All Shift' ? 600 : 400,
                                                '&:hover': {
                                                    backgroundColor: selectedShift === 'All Shift' ? '#BBDEFB' : '#F5F5F5',
                                                },
                                            }}
                                        >
                                            All Shifts
                                        </MenuItem>
                                    )}
                                </Menu>
                            </Box>
                            <Box className="info-row">
                                <Typography className="info-label">Audited By:</Typography>
                                <Box className="info-value">
                                    {isLoading ? <div className="audit-skeleton skeleton-info" /> : (operators || 'N/A')}
                                </Box>
                            </Box>
                        </>
                    )}
                </Box>

                {/* Right side cards */}
                {!hideCards && !isLoadingAcknowledgeAndVerified && !isLoadingStores && (
                    <Box className="no-pos-type-cards">
                        <ScratcherSalesTile
                            config={scratcherTileConfig}
                            isLoading={isLoading}
                            isAllShift={isAllShift}
                            posInputValue={posInputValues.scratcherSales}
                            secondaryPosInputValue={posInputValues.scratcherQty}
                            onPosInputChange={(next) => handlePosInputChange('scratcherSales', next)}
                            onSecondaryPosInputChange={(next) => handlePosInputChange('scratcherQty', next)}
                            localVerified={localVerified}
                            localAck={localAck}
                            onRequestDialog={handleRequestVerificationDialog}
                        />
                        <LottoSalesTile
                            config={lottoSalesTileConfig}
                            isLoading={isLoading}
                            isAllShift={isAllShift}
                            isLoadingManualDayEntry={isLoadingManualDayEntry}
                            isSavingManualDayEntry={isSavingManualDayEntry}
                            manualFormValues={manualFormValues}
                            onManualFieldChange={handleManualFieldChange}
                            posInputValue={posInputValues.lottoSales}
                            onPosInputChange={(next) => handlePosInputChange('lottoSales', next)}
                            localVerified={localVerified}
                            localAck={localAck}
                            onRequestDialog={handleRequestVerificationDialog}
                        />
                        <PayoutsTile
                            config={payoutsTileConfig}
                            isLoading={isLoading}
                            isAllShift={isAllShift}
                            isLoadingManualDayEntry={isLoadingManualDayEntry}
                            isSavingManualDayEntry={isSavingManualDayEntry}
                            manualFormValues={manualFormValues}
                            onManualFieldChange={handleManualFieldChange}
                            posInputValue={posInputValues.payouts}
                            onPosInputChange={(next) => handlePosInputChange('payouts', next)}
                            localVerified={localVerified}
                            localAck={localAck}
                            onRequestDialog={handleRequestVerificationDialog}
                        />
                        <OverallTile
                            config={overallTileConfig}
                            isLoading={isLoading}
                            isAllShift={isAllShift}
                            isLoadingManualDayEntry={isLoadingManualDayEntry}
                            isSavingManualDayEntry={isSavingManualDayEntry}
                            manualFormValues={manualFormValues}
                            onManualFieldChange={handleManualFieldChange}
                            localVerified={localVerified}
                            localAck={localAck}
                            onRequestDialog={handleRequestVerificationDialog}
                        />
                    </Box>
                )}
            </Box>
            {isAllShift && !isLoadingAcknowledgeAndVerified && !isLoadingStores && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', width: '100%', mb: 2, mt:1, gap: 1, flexWrap: 'wrap' }}>
                    {(manualDayEntryEditability.lotto || manualDayEntryEditability.collections) && (
                        <>
                            <CustomButton
                                variant="outlined"
                                size="small"
                                onClick={() => handleOpenManualEntryConfirmation('remove')}
                                disabled={isSavingManualDayEntry || isLoadingManualDayEntry}
                                sx={{
                                    '&.MuiButton-root .button-text': {
                                        color: '#2c6ca0',
                                    },
                                }}
                            >
                                Remove Manual Entries
                            </CustomButton>
                            <CustomButton
                                variant="contained"
                                size="small"
                                onClick={() => handleOpenManualEntryConfirmation('save')}
                                disabled={isSavingManualDayEntry || isLoadingManualDayEntry}
                            >
                                Save Manual Entries
                            </CustomButton>
                        </>
                    )}
                    {isBothConnected && isPosValueMissing && (
                        <CustomButton
                            variant="contained"
                            size="small"
                            onClick={() => setOpenSavePosDataConfirmation(true)}
                            disabled={isSavePosDataDisabled}
                        >
                            Save Manual Pos Entries
                        </CustomButton>
                    )}
                    {canShowVerifyAction ? (
                        <CustomButton
                            variant="contained"
                            size="small"
                            onClick={() => handleRequestVerificationDialog({ mode: 'verify', nextVerified: true, nextAck: localAck })}
                        >
                            Verify Daily Reconciliation
                        </CustomButton>
                    ) : null}
                    {canShowManagerNoteAction ? renderAddNoteButton() : null}
                </Box>
            )}
            <AuditVerificationDialog
                open={openVerificationDialog}
                onClose={() => {
                    setOpenVerificationDialog(false);
                }}
                storeId={storeId}
                date={date}
                mode={verificationMode}
                isVerified={pendingVerified}
                isAck={pendingAck}
                existingNote={verifiedNote}
                managerNote={managerNote}
                cashierShiftNotes={acknowledgeAndVerifiedModel?.cashierShiftNotes || []}
                onSubmit={(data) => {
                    setLocalVerified(true);
                    setLocalAck(pendingAck);
                    console.log(`Audit ${verificationMode} submitted:`, data);
                }}
            />
            <ManualEntryConfirmationDialog
                open={Boolean(manualEntryConfirmationAction && manualEntryConfirmationContent)}
                title={manualEntryConfirmationContent?.title || ''}
                message={manualEntryConfirmationContent?.message || ''}
                confirmButtonText={manualEntryConfirmationContent?.confirmButtonText || ''}
                onClose={handleCloseManualEntryConfirmation}
                onConfirm={handleConfirmManualEntryAction}
                disabled={isSavingManualDayEntry || isLoadingManualDayEntry}
                isLoading={isSavingManualDayEntry}
            />
            <ManualEntryConfirmationDialog
                open={openSavePosDataConfirmation}
                title="Save POS Manual Entries"
                message="Are you sure you want to add POS Manual Entries?"
                confirmButtonText="Save"
                onClose={() => setOpenSavePosDataConfirmation(false)}
                onConfirm={handleConfirmSavePosData}
                disabled={upsertPosManualDayEntryMutation.isPending}
                isLoading={upsertPosManualDayEntryMutation.isPending}
            />
            <AddNote
                open={openAddNoteDialog}
                onClose={() => setOpenAddNoteDialog(false)}
                storeId={storeId}
                date={date}
                existingNote={managerNote}
                isViewMode={hasNote}
                isVerified={isVerifiedFromAPI}
                isAck={isAckFromAPI}
                cashierShiftNotes={acknowledgeAndVerifiedModel?.cashierShiftNotes || []}
                onSubmit={(data) => {
                    console.log('Note submitted:', data);
                }}
            />
        </>
    );
};

export default AuditSummary;
